import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import xrange from "highcharts/modules/xrange";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import { useGetAttananceStaffDhashboardQuery } from "../../../../Api/attoDeskApi";
import dayjs, { Dayjs } from "dayjs";

xrange(Highcharts);

Highcharts.setOptions({
  time: {
    useUTC: false,
  },
});

interface AttendanceEntry {
  date: string;
  schedules: { start: Date; end: Date }[];
  actualPunches?: { start: Date; end: Date }[];
}
interface ITeampopup {
  staffId: string;
  selectedStartDate: Dayjs;
  selectedEndDate: Dayjs;
}

const AttendanceTimeline = ({
  staffId,
  selectedStartDate,
  selectedEndDate,
}: ITeampopup) => {
  const theme = useTheme();
  const { data: dashboardData, isLoading: dashboardIsLoading } =
    useGetAttananceStaffDhashboardQuery({
      id: String(staffId),
      startDate: selectedStartDate.format("YYYY-MM-DD"),
      endDate: selectedEndDate.format("YYYY-MM-DD"),
    });

  const attendanceData = useMemo(() => {
    return (dashboardData?.data as AttendanceEntry[]) || [];
  }, [dashboardData?.data]);

  const categories = attendanceData?.map((entry) => entry.date).reverse() || [];

  const scheduleSeries = attendanceData.flatMap((entry, index) =>
    entry.schedules.map((interval) => ({
      x: dayjs(interval.start).valueOf(),
      x2: dayjs(interval.end).valueOf(),
      y: attendanceData.length - 1 - index,
    }))
  );

  const actualSeries = attendanceData.flatMap((entry, index) =>
    (entry.actualPunches || []).map((interval) => ({
      x: dayjs(interval.start).valueOf(),
      // x2: dayjs(interval.end).valueOf(),
      x2: interval.end ? dayjs(interval.end).valueOf() : dayjs().valueOf(),
      y: attendanceData.length - 1 - index,
    }))
  );

  const options: Highcharts.Options = {
    chart: { type: "xrange", backgroundColor: theme.palette.cardAdd.bgColor },
    title: {
      text: "Employee Attendance Timeline",
      style: {
        // color: primaryColor,
        fontSize: "0.9375rem",
        color: theme.palette.heading.headingColor,
      },
    },
    // xAxis: {
    //   type: "datetime",
    //   title: {
    //     text: "Time",
    //     style: {
    //       fontSize: "0.75rem",
    //       color: theme.palette.heading.headingColor,
    //     },
    //   },
    //   tickInterval: 60 * 60 * 1000,
    //   labels: {
    //     format: "{value:%H:%M}",
    //     rotation: -45,
    //     align: "right",
    //     style: {
    //       fontSize: "0.75rem",
    //       color: theme.palette.heading.headingColor,
    //     },
    //   },
    // },
    xAxis: {
      type: "datetime",
      labels: {
        // format: "{value:%m-%d %H:%M}",
        format: "{value:%m-%d %I:%M %p}",
        rotation: -45,
        align: "right",
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
      tickInterval: 2 * 60 * 60 * 1000,
      // gridLineWidth: 0,
    },

    yAxis: {
      categories,
      title: {
        text: "Date",
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
      reversed: true,
      labels: {
        style: {
          fontSize: "0.75rem",
          color: theme.palette.heading.headingColor,
        },
      },
      // gridLineWidth: 0,
      // plotBands: [],
    },
    legend: {
      itemStyle: {
        fontSize: "0.75rem",
        color: theme.palette.heading.headingColor,
      },
    },
    // series: [
    //   {
    //     name: "Schedule",
    //     type: "xrange",
    //     pointWidth: 20,
    //     data: scheduleSeries,
    //     color: "#7cb5ec",
    //     dataLabels: { enabled: false },
    //   },
    //   {
    //     name: "Actual Punch",
    //     type: "xrange",
    //     pointWidth: 12,
    //     data: actualSeries,
    //     color: "#90ed7d",
    //     dataLabels: { enabled: false },
    //   },
    // ],
    series: [
      {
        name: "Actual Punch",
        type: "xrange",
        pointWidth: 12,
        data: actualSeries,
        colorByPoint: false,
        color: "#008000",
        borderColor: "#008000",
        dataLabels: { enabled: false },
      },
      {
        name: "Schedule",
        type: "xrange",
        pointWidth: 12,
        data: scheduleSeries,
        colorByPoint: false,
        color: "#FFA500",
        borderColor: "#FFA500",
        dataLabels: { enabled: false },
      },
    ],
    tooltip: {
      pointFormatter: function () {
        const point = this as any;
        return `
          <b>${point.series.name}</b><br/>
          Start: ${Highcharts.dateFormat("%e %b, %I:%M %p", point.x)}<br/>
          End: ${Highcharts.dateFormat("%e %b, %I:%M %p", point.x2)}
        `;
      },
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default AttendanceTimeline;
